/**
 * usePinnedPost — pin/unpin posts on profiles and community spaces
 *
 * Uses kind 30078 (NIP-78 application-specific data):
 *
 *   Profile pin:   d = "bchnostr/pinned-post"
 *   Community pin: d = "bchnostr/community-pin/<communityId>"
 *
 * Content: JSON { eventId: "<event-id>" }  (or "" to unpin)
 *
 * Since 30078 is parameterized-replaceable, each author can only
 * have ONE pin per d-tag — publishing a new one replaces the old.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish } from './useNostrPublish';

const PIN_KIND = 30078;
const PROFILE_PIN_DTAG = 'bchnostr/pinned-post';

function communityPinDtag(communityId: string) {
  return `bchnostr/community-pin/${communityId}`;
}

// ── Profile pinned post ─────────────────────────────────────────

export function useProfilePin(pubkey?: string) {
  const { publish } = useNostrPublish();

  const filters = useMemo(() =>
    pubkey ? [{ kinds: [PIN_KIND], authors: [pubkey], '#d': [PROFILE_PIN_DTAG], limit: 5 }] : [],
  [pubkey]);

  const { events, loading, refetch } = useNostrQuery({
    filters,
    enabled: !!pubkey,
  });

  const pinnedEventId = useMemo(() => {
    if (events.length === 0) return null;
    // Get the newest event (latest pin action)
    const sorted = [...events].sort((a, b) => b.created_at - a.created_at);
    try {
      const data = JSON.parse(sorted[0].content);
      return data.eventId || null;
    } catch {
      return null;
    }
  }, [events]);

  const pin = useCallback(async (eventId: string) => {
    if (!pubkey) return;
    await publish({
      kind: PIN_KIND,
      content: JSON.stringify({ eventId }),
      tags: [
        ['d', PROFILE_PIN_DTAG],
        ['e', eventId],
        ['t', 'bchnostr-pin'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    });
    refetch();
  }, [pubkey, publish, refetch]);

  const unpin = useCallback(async () => {
    if (!pubkey) return;
    await publish({
      kind: PIN_KIND,
      content: '',
      tags: [
        ['d', PROFILE_PIN_DTAG],
        ['t', 'bchnostr-pin'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    });
    refetch();
  }, [pubkey, publish, refetch]);

  return { pinnedEventId, loading, pin, unpin, refetch };
}

// ── Community pinned post ───────────────────────────────────────

export function useCommunityPin(communityId?: string, moderatorPubkeys?: string[]) {
  const { publish } = useNostrPublish();
  const dtag = communityId ? communityPinDtag(communityId) : '';

  // Query pin events from all moderators of the community
  const filters = useMemo(() => {
    if (!communityId || !moderatorPubkeys?.length) return [];
    return [{ kinds: [PIN_KIND], authors: moderatorPubkeys, '#d': [dtag], limit: 10 }];
  }, [communityId, moderatorPubkeys, dtag]);

  const { events, loading, refetch } = useNostrQuery({
    filters,
    enabled: !!communityId && !!moderatorPubkeys?.length,
  });

  const pinnedEventId = useMemo(() => {
    if (events.length === 0) return null;
    const sorted = [...events].sort((a, b) => b.created_at - a.created_at);
    try {
      const data = JSON.parse(sorted[0].content);
      return data.eventId || null;
    } catch {
      return null;
    }
  }, [events]);

  const pin = useCallback(async (eventId: string) => {
    if (!communityId) return;
    await publish({
      kind: PIN_KIND,
      content: JSON.stringify({ eventId }),
      tags: [
        ['d', dtag],
        ['e', eventId],
        ['a', communityId],
        ['t', 'bchnostr-pin'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    });
    refetch();
  }, [communityId, dtag, publish, refetch]);

  const unpin = useCallback(async () => {
    if (!communityId) return;
    await publish({
      kind: PIN_KIND,
      content: '',
      tags: [
        ['d', dtag],
        ['a', communityId],
        ['t', 'bchnostr-pin'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    });
    refetch();
  }, [communityId, dtag, publish, refetch]);

  return { pinnedEventId, loading, pin, unpin, refetch };
}

